import { loanBackendUrl } from '../config/taskApi';

const MOBILE_REGEX = /^[6-9]\d{9}$/;
const DEFAULT_DEBOUNCE_MS = 700;

export const saveEnquiryUrl = () => loanBackendUrl('saveEnquiry.php');

export const isValidEnquiryMobile = (mobile) =>
  MOBILE_REGEX.test(String(mobile || '').replace(/\D/g, '').slice(-10));

const normalizeMobile = (mobile) => String(mobile || '').replace(/\D/g, '').slice(-10);

const pick = (value) => {
  if (value === undefined || value === null) return undefined;
  const text = String(value).trim();
  return text ? text : undefined;
};

const toFlag01 = (value) => {
  if (value === 1 || value === '1' || value === true) return 1;
  return 0;
};

/**
 * Build CUMULATIVE enquiry payload from all fields filled so far.
 * Never includes OTP or secrets.
 */
export const buildEnquiryPayload = (formData = {}) => {
  const mobile = normalizeMobile(formData.Mobile || formData.mobile);
  if (!isValidEnquiryMobile(mobile)) return null;

  const name =
    pick(formData.Name) ||
    pick(formData.name) ||
    [formData.first_name, formData.middle_name, formData.last_name, formData.sur_name]
      .map((part) => pick(part))
      .filter(Boolean)
      .join(' ') ||
    undefined;

  // Always send cumulative base + mobile_verified flag.
  const payload = {
    Mobile: mobile,
    mobile,
    mobile_verified: toFlag01(
      formData.mobile_verified ?? formData.mobileVerified
    ),
  };

  if (name) {
    payload.Name = name;
    payload.name = name;
  }

  const firstName = pick(formData.first_name);
  if (firstName) payload.first_name = firstName;

  const email =
    pick(formData.email) ||
    pick(formData.Email) ||
    pick(formData.email_personal);
  if (email) {
    payload.email = email;
    payload.Email = email;
    payload.email_personal = email;
  }

  const loanAmount = pick(formData.loan_amount ?? formData.loanAmount);
  if (loanAmount) {
    payload.loan_amount = loanAmount;
    payload.loanAmount = loanAmount;
  }

  const cityId = pick(formData.city_id ?? formData.cityId);
  if (cityId) payload.city_id = cityId;

  const cityName = pick(formData.city_name ?? formData.city);
  if (cityName) {
    payload.city = cityName;
    payload.city_name = cityName;
  }

  const stateId = pick(formData.state_id ?? formData.stateId);
  if (stateId) payload.state_id = stateId;

  const pincode = pick(formData.pincode ?? formData.pin);
  if (pincode) {
    payload.pincode = pincode;
    payload.pin = pincode;
  }

  const pan = pick(formData.pan ?? formData.pancard);
  if (pan) {
    const panValue = pan.toUpperCase();
    payload.pan = panValue;
    payload.pancard = panValue;
  }

  const aadhaar = pick(
    formData.aadhaar ??
      formData.aadhaar_number ??
      formData.aadhaarNumber
  );
  if (aadhaar) {
    const digits = aadhaar.replace(/\D/g, '');
    if (digits.length === 12) {
      payload.aadhaar = digits;
      payload.aadhaar_number = digits;
    }
  }

  const step = pick(formData.step);
  if (step) payload.step = step;

  const ip = pick(formData.ip);
  if (ip) payload.ip = ip;

  const coordinates = pick(formData.coordinates ?? formData.geo_coordinates);
  if (coordinates) {
    payload.coordinates = coordinates;
    payload.geo_coordinates = coordinates;
  }

  const remarks = pick(formData.remarks ?? formData.message);
  if (remarks) {
    payload.remarks = remarks;
    payload.message = remarks;
  }

  return payload;
};

/**
 * Fire-and-forget enquiry save. Never throws to callers.
 * OTP must never be passed in formData.
 */
export const saveEnquiryDraft = async (formData, options = {}) => {
  try {
    const payload = buildEnquiryPayload(formData);
    if (!payload) return { skipped: true, reason: 'invalid_mobile' };

    const response = await fetch(saveEnquiryUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: Boolean(options.keepalive),
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    const ok =
      response.ok &&
      (data?.success === true || data?.status === 1 || data?.Status === 1);

    if (!ok) {
      console.warn('[saveEnquiryDraft] failed', {
        status: response.status,
        message: data?.message || data?.Message || null,
      });
    }

    return { ok, data, status: response.status };
  } catch (err) {
    console.warn('[saveEnquiryDraft] network error', err?.message || err);
    return { ok: false, error: err };
  }
};

/**
 * Debounced autosave controller for progressive form fills.
 * Always sends cumulative snapshot. Do NOT call on final lead submit.
 */
export const createEnquiryAutosave = ({
  getSnapshot,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  enabled = true,
} = {}) => {
  let timer = null;
  let lastFingerprint = '';
  let disposed = false;

  const fingerprint = (payload) => {
    try {
      return JSON.stringify(payload);
    } catch {
      return String(Date.now());
    }
  };

  const readPayload = () => {
    if (!enabled || disposed || typeof getSnapshot !== 'function') return null;
    try {
      const snapshot = getSnapshot();
      if (!snapshot || typeof snapshot !== 'object') return null;
      return buildEnquiryPayload(snapshot);
    } catch (err) {
      console.warn('[saveEnquiryDraft] snapshot error', err?.message || err);
      return null;
    }
  };

  const saveNow = (options = {}) => {
    if (disposed) return Promise.resolve({ skipped: true });
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }

    const payload = readPayload();
    if (!payload) return Promise.resolve({ skipped: true, reason: 'invalid_mobile' });

    const fp = fingerprint(payload);
    if (!options.force && fp === lastFingerprint) {
      return Promise.resolve({ skipped: true, reason: 'unchanged' });
    }

    lastFingerprint = fp;
    return saveEnquiryDraft(payload, options);
  };

  const scheduleSave = () => {
    if (disposed) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      saveNow();
    }, debounceMs);
  };

  const flushOnLeave = () => {
    if (disposed) return;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    const payload = readPayload();
    if (!payload) return;
    const fp = fingerprint(payload);
    if (fp === lastFingerprint) return;
    lastFingerprint = fp;
    saveEnquiryDraft(payload, { keepalive: true });
  };

  const dispose = () => {
    disposed = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return {
    scheduleSave,
    saveNow,
    flushOnLeave,
    dispose,
  };
};

export default saveEnquiryDraft;
